if (!customElements.get("cart-upsell")) {
  customElements.define(
    "cart-upsell",
    class CartUpsell extends HTMLElement {
      constructor() {
        super();
        this.variantData;
        this.addEventListener(
          "variantChange",
          this.handleVariantChange.bind(this),
        ); //user changes main product variant
      }

      connectedCallback() {
        if (this._initialised) return;
        this._initialised = true;

        try {
          this.variantData = JSON.parse(
            this.querySelector(".cart-upsell-data").innerHTML,
          );
        } catch (e) {
          console.error(e);
        }

        this.initTextInput();
      }

      handleVariantChange(e) {
        var current_variant = e.detail.currentVariant;

        if (!this.variantData) return; //payload failed to parse; leave the media alone
        if (!current_variant) return;

        //set media if current variant has changed
        this.changeMedia(current_variant);
      }

      changeMedia(variant) {
        var feature_image = variant.featured_image.src + "&width=150";

        var upsell_image = this.querySelector(".cart-item__image");

        if (!feature_image || !upsell_image) return;

        upsell_image.src = feature_image;
      }

      //--- Personalisation text input (GWP tiers only) ---

      initTextInput() {
        this.textHolder = this.querySelector("[data-upsell-text-input]");
        if (!this.textHolder) return;

        this.textInput = this.textHolder.querySelector("input[type=text]");
        this.hiddenInput = this.textHolder.querySelector(
          "[data-upsell-text-value]",
        );
        this.storageKey = this.textHolder.dataset.storageKey;
        this.maxLength = parseInt(this.textInput.dataset.maxlength, 10) || 0;
        this.forceUppercase = this.textInput.dataset.forceUppercase === "true";
        this.typeLabel = this.textInput.dataset.typeLabel || "";
        this.font = this.textInput.dataset.font || "";

        this.restoreSavedText();
        this.textInput.addEventListener("input", this.onTextInput.bind(this));

        //product-form dispatches this on the form before submitting; bubbles to us
        this.addEventListener("locket:validate", this.validateText.bind(this));
      }

      //Truncate, strip emoji and apply uppercase to the VALUE, so what is submitted
      //matches what is displayed (.text-upper is text-transform only).
      sanitiseText(value) {
        var cleaned = value.replace(/\p{Extended_Pictographic}/gu, "");

        if (this.forceUppercase) cleaned = cleaned.toUpperCase();
        if (this.maxLength) cleaned = cleaned.slice(0, this.maxLength);

        return cleaned;
      }

      //Mirrors getPositionSummaryValue() in configurator.js, so a GWP line reads like a
      //configured PDP line: "AB | Text | Antro". Font is the metaobject's first - the PDP default.
      composeValue(text) {
        if (text === "") return "";

        return [text, this.typeLabel, this.font].filter(Boolean).join(" | ");
      }

      //The visible field is not form-associated; the hidden input is what gets posted.
      //Disabling it while empty keeps a blank property off the cart line.
      syncHiddenValue(text) {
        if (!this.hiddenInput) return;

        var composed = this.composeValue(text);

        this.hiddenInput.value = composed;
        this.hiddenInput.disabled = composed === "";
      }

      onTextInput() {
        var cleaned = this.sanitiseText(this.textInput.value);

        if (cleaned !== this.textInput.value) {
          var pos =
            this.textInput.selectionStart -
            (this.textInput.value.length - cleaned.length);
          this.textInput.value = cleaned;
          this.textInput.setSelectionRange(pos, pos);
        }

        //clear any "please enter..." message raised by a previous submit attempt
        if (cleaned !== "")
          this.querySelector("product-form")?.handleErrorMessage();

        this.syncHiddenValue(cleaned);
        this.saveText(cleaned);
      }

      //Survives the cart re-render, which replaces this element's markup wholesale.
      saveText(value) {
        if (!this.storageKey) return;

        clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(() => {
          try {
            //An emptied field drops the key rather than storing a blank, so nothing
            //stale is left to restore over the placeholder on the next render.
            if (value.trim() === "") sessionStorage.removeItem(this.storageKey);
            else sessionStorage.setItem(this.storageKey, value);
          } catch (e) {
            //storage unavailable (private mode, quota) - persistence is best effort
          }
        }, 200);
      }

      restoreSavedText() {
        if (!this.storageKey) return;

        var saved;
        try {
          saved = sessionStorage.getItem(this.storageKey);
        } catch (e) {
          return;
        }

        //Whitespace-only counts as empty: restoring it would hide the placeholder
        //while still failing the required check.
        if (saved && saved.trim() !== "")
          this.textInput.value = this.sanitiseText(saved);

        this.syncHiddenValue(this.textInput.value);
      }

      clearSavedText() {
        if (!this.storageKey) return;

        clearTimeout(this._saveTimer);
        try {
          sessionStorage.removeItem(this.storageKey);
        } catch (e) {
          //nothing to clean up if storage is unavailable
        }
      }

      //The form is novalidate, so block the submit here. Re-sanitising catches values
      //set without an input event (autofill, extensions, restored state).
      validateText(event) {
        if (this.textInput.disabled) {
          //Locked tier - nothing to personalise, so post nothing either.
          if (this.hiddenInput) this.hiddenInput.disabled = true;
          return;
        }

        this.textInput.value = this.sanitiseText(this.textInput.value);
        this.syncHiddenValue(this.textInput.value);

        if (!this.textInput.hasAttribute("data-required")) return;
        if (this.textInput.value.trim() !== "") return;

        event.preventDefault();

        //No visible label to read from - the field name lives on data-field-label.
        //(aria-label carries the placeholder prompt, which reads wrong in a sentence.)
        var message =
          "Please enter your " + (this.textInput.dataset.fieldLabel || "text");

        this.querySelector("product-form")?.handleErrorMessage(message);
        this.textInput.focus();
      }
    },
  );
}
